import { useCallback } from 'react';

import { Button } from '@capsule/consumer-design-system';

import { useHistory } from 'react-router';
import { css } from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { Typography } from 'src/app/components/Elements/Typography';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import { MedicationList } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationList';
import {
  useLoggedInTransferFormData,
  useLoggedInTransferFormProgress,
} from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import * as paths from 'src/app/router/paths';
import { medications } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import { STEP_NAME } from '../types';

import { Divider, StyledLinkButton, StyledList, TransferTypeInfoColumn, TransferTypeInfoSection } from './styles';

const RenderlessViewAskYourDoctorScreenEvent = ({
  medicationNames: medication_names,
}: {
  medicationNames: string[];
}) => {
  useSegmentEvent('viewAskYourDoctorScreen', {
    shouldTrackOnMount: true,
    payload: { medication_names },
  });

  return null;
};

interface ConfirmationFormRendererProps {
  showHeart: (callback: () => void) => void;
}

export const LoggedInTransferConfirmation = ({ showHeart }: ConfirmationFormRendererProps) => {
  const { push } = useHistory();
  const { loggedInTransferFormData, setLoggedInTransferFormData } = useLoggedInTransferFormData();
  const { loggedInTransferFormProgress, setLoggedInTransferFormProgress } = useLoggedInTransferFormProgress();

  useSegmentEvent('viewTransferConfirmation', { shouldTrackOnMount: true });

  const transferableMeds = loggedInTransferFormData?.medications?.filter(
    med => !med.doctorRequiredForTransfer && !!med.eligibleForTransfer
  );
  const hasTransferableMedications = transferableMeds && transferableMeds.length > 0;

  const clearTransferForm = useCallback(() => {
    setLoggedInTransferFormProgress({});
    setLoggedInTransferFormData({});
  }, [setLoggedInTransferFormProgress, setLoggedInTransferFormData]);

  const handleConfirmationButtonOnClick = useCallback(() => {
    const clearAndRedirect = () => {
      clearTransferForm();
      push(medications.route);
    };
    showHeart(clearAndRedirect);
  }, [showHeart, clearTransferForm, push]);

  const isDoctorFlow = !!loggedInTransferFormProgress?.steps?.find(step => step === STEP_NAME.DOCTOR);
  const possessiveArticle = loggedInTransferFormData?.patientIsPrimary ? 'your' : 'their';

  const title =
    transferableMeds?.length === 0
      ? `Ask ${possessiveArticle} doctor to send ${possessiveArticle} prescriptions to Capsule`
      : `You’re all set`;

  /**
   * If no transferrable meds, prompt user to ask their doctor next time
   * Otherwise, change copy based on whether user went through a doctor or pharmacy flow
   * and have a fallback for when pharmacy name is not present.
   */
  const text =
    transferableMeds?.length === 0
      ? `The next time you speak to your doctor, tell them to send the following to Capsule:`
      : isDoctorFlow
      ? `We will contact ${possessiveArticle} doctor to move your medications over to Capsule. This can take 2-3 days.`
      : !!loggedInTransferFormData?.pharmacyName
      ? `Capsule will contact ${loggedInTransferFormData.pharmacyName} to transfer ${possessiveArticle} meds. This can take 1–2 days.`
      : `We will contact ${possessiveArticle} past pharmacy to move ${possessiveArticle} medications over to Capsule. This can take 1–2 days.`;

  return (
    <>
      <CapsuleIcon
        iconString={CAPSULE_ICON_STRINGS.check_circle_large_green}
        css={css`
          width: 72px;
          height: 72px;
          svg {
            width: 72px !important;
            height: 72px !important;
          }
        `}
      />
      <Step title={title} text={text} showPreviousButton={false}>
        <TransferTypeInfoSection>
          <CapsuleIcon
            css={css`
              align-self: start;
              svg {
                width: 24px;
                height: 24px;
                fill: ${props => props.theme.colors.capsuleBlue60};
              }
            `}
            iconString={isDoctorFlow ? CAPSULE_ICON_STRINGS.stethoscope_icon : CAPSULE_ICON_STRINGS.old_pharmacy}
          />
          <TransferTypeInfoColumn>
            <Typography
              variant="body"
              marginBottom={'s1'}
              css={css`
                margin-left: ${props => props.theme.space.s2} !important;
              `}
            >
              {isDoctorFlow ? loggedInTransferFormData?.doctorName : loggedInTransferFormData?.pharmacyName}
            </Typography>
            <Typography
              variant="body"
              css={css`
                margin-left: ${props => props.theme.space.s2} !important;
              `}
            >
              {isDoctorFlow
                ? loggedInTransferFormData?.doctorPhone
                : loggedInTransferFormData?.pharmacyAddress || loggedInTransferFormData?.pharmacyPhone}
            </Typography>
          </TransferTypeInfoColumn>
        </TransferTypeInfoSection>

        <StyledList>
          <MedicationList
            medications={loggedInTransferFormData?.medications?.map(med => med.primaryName) || []}
            hideMedicationHeader
          />
        </StyledList>

        <Button
          onClick={handleConfirmationButtonOnClick}
          variant="contained-secondary-lg"
          css={css`
            width: min-content;
          `}
        >
          DONE
        </Button>

        {hasTransferableMedications ? (
          <>
            <Divider />

            <Typography variant="h1" marginBottom={'s3'}>
              Did you know?
            </Typography>
            <Typography variant="body">
              Any doctor can send your prescriptions to Capsule. Just tell them your preferred pharmacy is "Capsule".
            </Typography>
            <StyledLinkButton
              type="submit"
              size="medium"
              style="secondary"
              onClick={clearTransferForm}
              to={paths.showYourDoctor.toPath()}
              block
            >
              SHOW ME HOW
            </StyledLinkButton>
          </>
        ) : (
          <RenderlessViewAskYourDoctorScreenEvent
            medicationNames={loggedInTransferFormData?.medications?.map(med => med.primaryName) || []}
          />
        )}
      </Step>
    </>
  );
};
